import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import classnames from "classnames/bind";
import FocusLock, { InFocusGuard } from "react-focus-lock";

import { navigationPropType } from "@template/state/modules/navigation";
import {
  MALE,
  FEMALE,
  genderPropType,
  viewportPropType,
  SMALL_VIEWPORT,
  MEDIUM_VIEWPORT
} from "@template/state/modules/layout";
import FloorNavigation from "@template/components/FloorNavigation";
import SidePrimaryNavigation from "../SidePrimaryNavigation";
import SideSecondaryNavigation from "../SideSecondaryNavigation";
import withTranslation from "@template/components/translation";
import withViewport from "@template/components/Viewport/withViewport";
import Footer from "@template/components/Footer";
import styles from "./index.css";
import globalEventHandler, { events, keys } from "@template/globalEventHandler";
import { BURGER_MENU_BUTTON_DATA_TEST_ID } from "@template/components/Burger/Burger";

const cx = classnames.bind(styles);

class SidePanel extends PureComponent {
  shouldAnimate = false;

  constructor(props) {
    super(props);

    this.saveDialog = this.saveDialog.bind(this);
    this.closeSidePanel = this.closeSidePanel.bind(this);
  }

  componentDidMount() {
    this.dialog &&
      this.dialog.addEventListener("transitionend", this.handleTransitionEnd);

    this.globalKeyDownListener = globalEventHandler.addListener(
      events.keyDown,
      event => {
        if (event.key === keys.escape) {
          this.props.isSidePanelOpen &&
            this.closeSidePanel(event.originalEvent);
        }
      }
    );
  }

  componentWillUnmount() {
    this.dialog &&
      this.dialog.removeEventListener(
        "transitionend",
        this.handleTransitionEnd
      );

    this.globalKeyDownListener && this.globalKeyDownListener.remove();
  }

  handleTransitionEnd = ({ target, currentTarget }) => {
    this.triggerRedraw();
    if (target === currentTarget) {
      this.props.clearNavActiveItem();
    }
  };

  triggerRedraw() {
    this.dialog.style.display = "none";
    this.dialog.offsetHeight;
    this.dialog.style.display = "block";
  }

  saveDialog(c) {
    this.dialog = c;
  }

  closeSidePanel() {
    this.props.clearNavActiveItem();
    this.props.closeSidePanel();
    this.props.focusLastActiveElement(this.props.lastActiveElements, {
      forceElementFocus: `[data-testid="${BURGER_MENU_BUTTON_DATA_TEST_ID}"]`
    });
  }

  render() {
    const {
      formatTranslation,
      isSidePanelOpen,
      isVisible,

      navActiveItem,
      setNavActiveItem,
      clearNavActiveItem,
      currentGender,
      maleItems,
      femaleItems,
      viewport,
      focusLastActiveElement,
      lastActiveElements
    } = this.props;

    this.shouldAnimate = this.shouldAnimate || (isSidePanelOpen && isVisible);

    const navProps = {
      currentGender,
      navActiveItem,
      setNavActiveItem,
      clearNavActiveItem,
      focusLastActiveElement,
      lastActiveElements
    };

    const viewable = [SMALL_VIEWPORT, MEDIUM_VIEWPORT];
    return (
      viewable.includes(viewport) && (
        <div>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={formatTranslation("sidepanel_navigation_label")}
            aria-hidden={isSidePanelOpen ? null : true}
            ref={this.saveDialog}
            className={cx(styles.dialog, {
              [styles.dialog__visible]: isSidePanelOpen && isVisible,
              [styles.dialog__animate]: this.shouldAnimate
            })}
            data-testid="sidepanel"
          >
            <div className={styles.container}>
              <FocusLock
                disabled={!!(!isSidePanelOpen || navActiveItem)}
                className={styles.primarySideLockContainer}
                lockProps={{ "aria-hidden": navActiveItem ? true : null }}
                noFocusGuards={true}
              >
                <InFocusGuard>
                  <button
                    className={styles.close}
                    onClick={this.closeSidePanel}
                    aria-label={formatTranslation(
                      "sidepanel_close_navigation_label"
                    )}
                    ref={this.getCloseButtonRef}
                    data-testid="close-button"
                  />
                  <div
                    className={cx(styles.primarySideContainer, {
                      [styles.primarySideContainer__hidden]: !!navActiveItem,
                      [styles.primarySideContainer__animate]: this.shouldAnimate
                    })}
                  >
                    <FloorNavigation viewable={viewable} />
                    <div className={styles.content}>
                      <SidePrimaryNavigation
                        {...navProps}
                        items={femaleItems}
                        forGender={FEMALE}
                      />
                      <SidePrimaryNavigation
                        {...navProps}
                        items={maleItems}
                        forGender={MALE}
                      />
                      <Footer isAccordion={true} />
                    </div>
                  </div>
                </InFocusGuard>
              </FocusLock>
              <FocusLock
                disabled={!navActiveItem}
                className={styles.secondarySideLockContainer}
                noFocusGuards={true}
              >
                <InFocusGuard>
                  {navActiveItem && (
                    <button
                      className={styles.close}
                      onClick={this.closeSidePanel}
                      aria-label={formatTranslation(
                        "sidepanel_close_navigation_label"
                      )}
                      ref={this.getCloseButtonRef}
                      data-testid="close-button"
                    />
                  )}
                  <SideSecondaryNavigation
                    {...navProps}
                    items={femaleItems}
                    forGender={FEMALE}
                  />
                  <SideSecondaryNavigation
                    {...navProps}
                    items={maleItems}
                    forGender={MALE}
                  />
                </InFocusGuard>
              </FocusLock>
            </div>
          </div>
          <aside
            className={cx(styles.overlay, {
              [styles.overlay__show]: isSidePanelOpen && isVisible,
              [styles.overlay__animate]: this.shouldAnimate
            })}
            role="button"
            onClick={this.closeSidePanel}
            onTouchMove={event => {
              event.preventDefault();
              return false;
            }}
            aria-hidden="true"
          />
        </div>
      )
    );
  }
}

SidePanel.propTypes = {
  isSidePanelOpen: PropTypes.bool.isRequired,
  closeSidePanel: PropTypes.func.isRequired,
  clearNavActiveItem: PropTypes.func.isRequired,
  setNavActiveItem: PropTypes.func.isRequired,
  navActiveItem: PropTypes.string,
  currentGender: genderPropType.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
  maleItems: PropTypes.arrayOf(navigationPropType).isRequired,
  femaleItems: PropTypes.arrayOf(navigationPropType).isRequired,
  viewport: viewportPropType,
  focusLastActiveElement: PropTypes.func,
  lastActiveElements: PropTypes.arrayOf(PropTypes.string)
};

export default withViewport(withTranslation(SidePanel));
